import api from './api';

/**
 * AI Subscription API Service
 * Follows Single Responsibility Principle - handles only subscription-related API calls
 * Follows Dependency Inversion - depends on abstract api instance
 */

export const subscriptionAPI = {
  // ==================== SUBSCRIPTION PLANS ====================
  
  /**
   * Get all available subscription plans
   * @returns {Promise} List of subscription plans
   */
  getPlans: () => api.get('/ai-subscription/plans'),

  /**
   * Get specific plan details
   * @param {number} planId - Plan ID
   * @returns {Promise} Plan details with features
   */
  getPlanDetails: (planId) => api.get(`/ai-subscription/plans/${planId}`),

  // ==================== USER SUBSCRIPTION ====================
  
  /**
   * Get current user's subscription status
   * @param {number} userId - User ID
   * @returns {Promise} Current subscription details
   */
  getCurrentSubscription: (userId) => api.get(`/ai-subscription/user/${userId}/current`),

  /**
   * Subscribe to a plan
   * @param {Object} data - {user_id, plan_id, billing_cycle}
   * @returns {Promise} Subscription creation response
   */
  subscribe: (data) => api.post('/ai-subscription/subscribe', data),

  /**
   * Upgrade subscription plan
   * @param {Object} data - {subscription_id, new_plan_id}
   * @returns {Promise} Upgrade response
   */
  upgradeSubscription: (data) => api.post('/ai-subscription/upgrade', data),

  /**
   * Downgrade subscription plan
   * @param {Object} data - {subscription_id, new_plan_id}
   * @returns {Promise} Downgrade response
   */
  downgradeSubscription: (data) => api.post('/ai-subscription/downgrade', data),

  /**
   * Cancel subscription
   * @param {number} subscriptionId - Subscription ID
   * @param {Object} data - {user_id, reason}
   * @returns {Promise} Cancellation response
   */
  cancelSubscription: (subscriptionId, data) => api.post(`/ai-subscription/subscription/${subscriptionId}/cancel`, data),

  /**
   * Reactivate cancelled subscription
   * @param {number} subscriptionId - Subscription ID
   * @returns {Promise} Reactivation response
   */
  reactivateSubscription: (subscriptionId) => api.post(`/ai-subscription/${subscriptionId}/reactivate`),

  // ==================== PAYMENT ====================
  
  /**
   * Create Razorpay order for subscription payment
   * @param {Object} data - {plan_id, billing_cycle, user_id}
   * @returns {Promise} Razorpay order details
   */
  createPaymentOrder: (data) => api.post('/ai-subscription/payment/create-order', data),

  /**
   * Verify payment and activate subscription
   * @param {Object} data - {razorpay_order_id, razorpay_payment_id, razorpay_signature, ...}
   * @returns {Promise} Payment verification response
   */
  verifyPayment: (data) => api.post('/ai-subscription/payment/verify', data),

  /**
   * Get payment history
   * @param {number} userId - User ID
   * @param {number} limit - Results limit
   * @returns {Promise} Payment history
   */
  getPaymentHistory: (userId, limit = 20) => api.get(`/ai-subscription/user/${userId}/payments?limit=${limit}`),

  // ==================== USAGE TRACKING ====================
  
  /**
   * Get current month usage statistics
   * @param {number} userId - User ID
   * @returns {Promise} Usage statistics
   */
  getUsageStats: (userId) => api.get(`/ai-subscription/user/${userId}/usage`),

  /**
   * Check feature availability and remaining usage
   * @param {number} userId - User ID
   * @param {string} featureType - Feature type (quiz, doubt_solver, etc.)
   * @returns {Promise} Feature availability status
   */
  checkFeatureAccess: (userId, featureType) => api.get(`/ai-subscription/user/${userId}/feature/${featureType}/access`),

  /**
   * Log feature usage
   * @param {Object} data - {user_id, feature_type, metadata}
   * @returns {Promise} Usage log response
   */
  logUsage: (data) => api.post('/ai-subscription/usage/log', data),

  // ==================== AI FEATURES ====================
  
  /**
   * Generate AI Quiz
   * @param {Object} data - {user_id, course_id, topic, num_questions, difficulty}
   * @returns {Promise} Generated quiz data
   */
  generateAIQuiz: (data) => api.post('/ai-subscription/features/quiz/generate', data),

  /**
   * Ask AI Doubt Solver
   * @param {Object} data - {user_id, question, course_id, input_type, image_url}
   * @returns {Promise} AI response
   */
  askDoubt: (data) => api.post('/ai-subscription/features/doubt-solver', data),

  /**
   * Generate study notes summary
   * @param {Object} data - {user_id, document_url, summary_type}
   * @returns {Promise} Summarized notes
   */
  generateSummary: (data) => api.post('/ai-subscription/features/summary/generate', data),

  /**
   * Create flashcards from content
   * @param {Object} data - {user_id, content, topic}
   * @returns {Promise} Generated flashcards
   */
  generateFlashcards: (data) => api.post('/ai-subscription/features/flashcards/generate', data),

  /**
   * Generate personalized study plan
   * @param {Object} data - {user_id, courses, goals, available_hours}
   * @returns {Promise} Study plan
   */
  generateStudyPlan: (data) => api.post('/ai-subscription/features/study-plan/generate', data),

  /**
   * Generate mock test (Premium only)
   * @param {Object} data - {user_id, exam_type, syllabus, duration}
   * @returns {Promise} Mock test data
   */
  generateMockTest: (data) => api.post('/ai-subscription/features/mock-test/generate', data),

  // ==================== SAVED CONTENT ====================
  
  /**
   * Get user's saved AI content
   * @param {number} userId - User ID
   * @param {string} contentType - Content type (quiz, flashcards, etc.)
   * @param {number} limit - Results limit
   * @returns {Promise} Saved content list
   */
  getSavedContent: (userId, contentType, limit = 20) => api.get(`/ai-subscription/user/${userId}/content/${contentType}?limit=${limit}`),

  /**
   * Save AI generated content
   * @param {Object} data - {user_id, content_type, content_data, title}
   * @returns {Promise} Save response
   */
  saveContent: (data) => api.post('/ai-subscription/content/save', data),

  /**
   * Delete saved content
   * @param {number} contentId - Content ID
   * @returns {Promise} Delete response
   */
  deleteContent: (contentId) => api.delete(`/ai-subscription/content/${contentId}`),

  // ==================== ANALYTICS (Premium) ====================
  
  /**
   * Get learning analytics
   * @param {number} userId - User ID
   * @param {string} period - Time period (week, month, all)
   * @returns {Promise} Analytics data
   */
  getAnalytics: (userId, period = 'month') => api.get(`/ai-subscription/user/${userId}/analytics?period=${period}`),

  /**
   * Get personalized recommendations
   * @param {number} userId - User ID
   * @returns {Promise} AI recommendations
   */
  getRecommendations: (userId) => api.get(`/ai-subscription/user/${userId}/recommendations`),
};

export default subscriptionAPI;
